import React, { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineCommentExclamationIcon from 'LuxKit/Icons/line/LineCommentExclamationIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ContactContext from 'contexts/contactContext'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import { rem } from 'polished'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalContext from 'contexts/ModalContext'
import type { ChatHistoryModalResult } from '../History/ChatsHistoryModal'
import ChatsHistoryModal from '../History/ChatsHistoryModal'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { NewChatModalResult } from './NewChatModal'
import NewChatModal from './NewChatModal'
import { createNewChat, postMessageFeedback, setCurrentChatId } from 'actions/supportAssistant/supportAssistantActions'
import FeedbackModal from './FeedbackModal'
import { SocketContext } from 'supportAssistant/contexts/SocketContext'

const HeaderGroup = styled(Group)`
  height: ${rem(60)};
`

function ChatHeader() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { onLogin } = useContext(SocketContext)

  const customerId = useAppSelector(state => state.auth.account.memberId)
  const currentChatId = useAppSelector(state => state.supportAssistant.currentChatId) ?? 'default'

  const showModal = useContext(ModalContext)

  const { value: open, toggle, off } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local

  const handleNewChatClick = useCallback(async() => {
    if (!customerId && currentChatId) {
      const result = await showModal<NewChatModalResult>(<NewChatModal />)
      if (result?.triggerLogin) {
        onLogin()
      }
    } else {
      dispatch(createNewChat())
    }
  }, [currentChatId, customerId, dispatch, onLogin, showModal])

  const showChatHistoryModal = useCallback(async() => {
    const result = await showModal<ChatHistoryModalResult>(<ChatsHistoryModal />)
    if (result?.chatId) {
      dispatch(setCurrentChatId(result.chatId))
    }
  }, [dispatch, showModal])

  const onFeedbackClick = useCallback(async() => {
    const userFeedback = await showModal<undefined | any>(<FeedbackModal />)

    if (userFeedback?.issues?.length) {
      dispatch(postMessageFeedback({
        chatId: currentChatId,
        customerId,
        ...userFeedback,
      }))
    }
  }, [currentChatId, customerId, dispatch, showModal])

  return (
    <HeaderGroup direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <Group direction="horizontal">
        <CSSBreakpoint min="tablet">
          <TextButton
            kind="tertiary"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'rHuv9WXssn',
              defaultMessage: 'show history',
              description: 'Aria label for button to show chat history on tablet',
            })}
            onClick={showChatHistoryModal}
            startIcon={<LineBarsIcon />}
          >
            {intl.formatMessage({
              id: 'nUs09cKFpf',
              defaultMessage: 'Chat History',
              description: 'Button text to open chat history panel',
            })}
          </TextButton>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <IconButton
            kind="tertiary"
            variant="dark"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'q9RaT98SUX',
              defaultMessage: 'show history',
              description: 'Aria label for button to show chat history on mobile',
            })}
            onClick={showChatHistoryModal}
          >
            <LineBarsIcon />
          </IconButton>
          <IconButton
            kind="tertiary"
            variant="dark"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'EmTXnaC4pJ',
              defaultMessage: 'call support',
              description: 'Aria label for button to call support on mobile',
            })}
            href={`tel:${localContact.number}`}
            rel="nofollow"
          >
            <LinePhoneIcon />
          </IconButton>
        </CSSBreakpoint>
      </Group>

      <CSSBreakpoint min="tablet">
        <HeadingTextBlock variant="heading6">
          {intl.formatMessage({
            id: 'C4XpDbFgXo',
            defaultMessage: 'Luxury Escapes Support',
            description: 'Heading text for the support chat header',
          })}
        </HeadingTextBlock>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <ClickableLink to="/">
          <LuxuryEscapesSmallLogo height={32} width={32} />
        </ClickableLink>
      </CSSBreakpoint>

      <Group direction="horizontal" horizontalAlign="end">
        <CSSBreakpoint min="tablet">
          <TextButton
            kind="tertiary"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: '1YyOysafw9',
              defaultMessage: 'open new chat',
              description: 'Aria label for button to open a new chat on tablet',
            })}
            onClick={handleNewChatClick}
            startIcon={<LinePlusIcon />}
          >
            {intl.formatMessage({
              id: 'abQCMCvtLX',
              defaultMessage: 'New Chat',
              description: 'Button text to start a new chat',
            })}
          </TextButton>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <IconButton
            kind="tertiary"
            variant="dark"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: '+CRx7lOv21',
              defaultMessage: 'open new chat',
              description: 'Aria label for button to open a new chat on mobile',
            })}
            onClick={handleNewChatClick}
          >
            <LinePlusIcon />
          </IconButton>
        </CSSBreakpoint>

        <IconButton
          kind="tertiary"
          variant="dark"
          data-testid="action-menu-button"
          aria-label={intl.formatMessage({
            id: '8T88rKCGpa',
            defaultMessage: 'chat action menu',
            description: 'Aria label for chat action overflow menu button',
          })}
          onClick={toggle}
          ref={triggerRef}
        >
          <LineEllipsisVIcon />
        </IconButton>
        <DropdownList
          size="S"
          open={open}
          triggerRef={triggerRef}
          anchorRef={triggerRef}
          onClose={off}
        >
          <ClickableListItem
            disabled={currentChatId === 'default'}
            size="medium"
            startIcon={<LineCommentExclamationIcon />}
            title={intl.formatMessage({
              id: 'j2Ao6OwXeC',
              defaultMessage: 'Report a problem',
              description: 'Menu item to report a problem with support chat',
            })}
            onClick={onFeedbackClick}
          />
        </DropdownList>
      </Group>
    </HeaderGroup>
  )
}

export default ChatHeader
